import type { ComponentPropsWithRef as ComponentProps } from 'react';
import React, { forwardRef, memo, useMemo } from 'react';

import type { ColorName } from 'styles/config/colors';
import { z } from 'zod';

import useDebouncedState from '@polyai/common/hooks/useDebouncedState';
import { upCaseFirstLetter } from '@polyai/common/lib/stringHelper';
import ConditionalWrapper from '@polyai/common/utils/conditionalWrapper';

import { cn } from 'utils/cn';
import { colorCssVar } from 'utils/colorCssVar';
import Clickable from '../Clickable';
import type { BaseIcon, IconSize } from '../Icons/BaseIcon';
import LoadingIcon from '../LoadingIcon';

const emojiSchema = z.string().emoji();

export enum ButtonVariant {
  BRAND = 'brand',
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  GHOST = 'ghost',
  SUCCESS = 'success',
  DANGER = 'danger',
  MAKE_IT_POP = 'make-it-pop',
  LINK = 'link',
}

export type ButtonLabelSize = 'small' | 'regular';

type Extends = ComponentProps<'button'>;

interface BaseProps extends Extends {
  'data-test-id'?: string;
  'data-dd-action'?: string;
  label: string;
  variant?: keyof typeof ButtonVariant;
  inverse?: boolean;
  size?: ButtonLabelSize;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  to?: string;
  iconColor?: ColorName;
  backgroundColor?: ColorName;
  iconSize?: keyof typeof IconSize;
  labelColor?: ColorName;
}

export type ButtonIcon = typeof BaseIcon | string | React.ReactNode;

export interface ButtonWithIconsProps extends BaseProps {
  // limiting the type to only accept Icon or emoji
  startAdornment?: ButtonIcon;
  endAdornment?: ButtonIcon;
}

export interface ButtonWithIconOnlyProps extends BaseProps {
  Icon: ButtonIcon;
  badge: boolean;
  badgeColor?: ColorName;
}

export type ButtonProps = ButtonWithIconsProps | ButtonWithIconOnlyProps;

export const variantToButtonIconColor: {
  [key in keyof typeof ButtonVariant]: ColorName;
} = {
  BRAND: 'iconButtonBrand',
  PRIMARY: 'iconPrimaryInverse',
  SECONDARY: 'iconSecondary',
  GHOST: 'iconPrimary',
  SUCCESS: 'iconPrimaryInverse',
  DANGER: 'iconPrimaryInverse',
  MAKE_IT_POP: 'iconPrimaryInverse',
  LINK: 'iconSecondary',
};

export const variantToInverseButtonIconColor: {
  [key in keyof typeof ButtonVariant]: ColorName;
} = {
  BRAND: 'iconButtonBrand',
  PRIMARY: 'iconButtonBrand',
  SECONDARY: 'iconPrimaryInverse',
  GHOST: 'iconPrimaryInverse',
  SUCCESS: 'iconPrimaryInverse',
  DANGER: 'iconPrimaryInverse',
  MAKE_IT_POP: 'iconPrimaryInverse',
  LINK: 'iconSecondaryInverse',
};

// Base button classes shared across normal and icon-only
const BASE_BUTTON =
  'relative py-xs2 px-sm2 text-center bg-background-primary text-primary-inverse rounded-[20px] transition-all duration-300 ease-[cubic-bezier(0.39,0.575,0.565,1)]';

const DISABLED_BUTTON = 'disabled:cursor-not-allowed';

const VARIANT_CLASSES: Record<keyof typeof ButtonVariant, string> = {
  BRAND:
    'text-button-brand bg-background-brand hover:bg-background-brand-hover focus:shadow-[inset_0_0_0_2px_var(--tw-border-focus)]',
  PRIMARY:
    'text-[var(--tw-button-primary-text)] bg-[var(--tw-button-primary-background)] hover:bg-[var(--tw-button-primary-background-hover)] focus:text-[var(--tw-button-primary-text)] focus:shadow-[inset_0_0_0_2px_var(--tw-border-focus)]',
  SECONDARY:
    'text-primary !bg-transparent shadow-[inset_0_0_0_1px_var(--tw-border-subtle)] hover:shadow-[inset_0_0_0_1px_var(--tw-border-strong)] focus:shadow-[inset_0_0_0_2px_var(--tw-border-focus)] disabled:!bg-transparent disabled:hover:!bg-transparent disabled:active:!bg-transparent disabled:focus:!bg-transparent disabled:shadow-[inset_0_0_0_1px_var(--tw-border-subtle)] disabled:hover:shadow-[inset_0_0_0_1px_var(--tw-border-subtle)] disabled:active:shadow-[inset_0_0_0_1px_var(--tw-border-subtle)] disabled:focus:shadow-[inset_0_0_0_1px_var(--tw-border-subtle)]',
  GHOST:
    'text-primary !bg-transparent border-none hover:!bg-background-hover focus:shadow-[inset_0_0_0_2px_var(--tw-border-focus)] disabled:!bg-transparent disabled:hover:!bg-transparent disabled:active:!bg-transparent disabled:focus:!bg-transparent',
  SUCCESS:
    '!text-primary-inverse bg-background-success hover:bg-background-success-hover focus:shadow-[inset_0_0_0_2px_var(--tw-border-focus)]',
  DANGER:
    '!text-primary-inverse bg-background-danger hover:bg-background-danger-hover focus:shadow-[inset_0_0_0_2px_var(--tw-border-focus)]',
  MAKE_IT_POP:
    'border-none outline-none transition-all duration-500 ease-[cubic-bezier(0.2,2.3,0.4,1.2)] before:content-[""] before:absolute before:z-[-1] before:inset-[-4px] before:filter before:blur-[0.05rem] before:rounded-[16px] before:transition-all before:duration-200 before:ease-in-out before:[animation:rotation_2s_forwards_infinite] after:content-[""] after:absolute after:z-[-1] after:inset-0 after:bg-background-primary after:filter after:blur-[0.1rem] after:rounded-[12px] hover:scale-105 hover:text-black hover:bg-background-hover',
  LINK: 'p-0 text-secondary !bg-transparent border-none rounded-none enabled:hover:border-b enabled:hover:border-secondary enabled:focus:border-b enabled:focus:border-secondary',
};

const VARIANT_INVERSE_CLASSES: Record<keyof typeof ButtonVariant, string> = {
  BRAND:
    'text-button-brand bg-background-brand hover:bg-background-brand-hover focus:shadow-[inset_0_0_0_2px_var(--tw-border-inverse-focus)]',
  PRIMARY:
    'text-button-brand bg-background-primary-inverse hover:bg-background-primary-inverse-hover focus:text-button-brand focus:shadow-[inset_0_0_0_2px_var(--tw-border-inverse-focus)]',
  SECONDARY:
    '!text-primary-inverse !bg-transparent shadow-[inset_0_0_0_1px_var(--tw-border-subtle-inverse)] hover:!bg-background-inverse-hover hover:shadow-[inset_0_0_0_1px_var(--tw-border-strong-inverse)] focus:shadow-[inset_0_0_0_2px_var(--tw-border-inverse-focus)] disabled:shadow-[inset_0_0_0_1px_var(--tw-border-strong-inverse)] disabled:hover:shadow-[inset_0_0_0_1px_var(--tw-border-strong-inverse)] disabled:active:shadow-[inset_0_0_0_1px_var(--tw-border-strong-inverse)] disabled:focus:shadow-[inset_0_0_0_1px_var(--tw-border-strong-inverse)]',
  GHOST:
    '!text-primary-inverse !bg-transparent border-none hover:bg-background-inverse-hover focus:shadow-[inset_0_0_0_2px_var(--tw-border-inverse-focus)] disabled:!bg-transparent disabled:hover:!bg-transparent disabled:active:!bg-transparent disabled:focus:!bg-transparent',
  SUCCESS:
    '!text-primary-inverse bg-background-success hover:bg-background-success-hover focus:shadow-[inset_0_0_0_2px_var(--tw-border-inverse-focus)]',
  DANGER:
    '!text-primary-inverse bg-background-danger hover:bg-background-danger-hover focus:shadow-[inset_0_0_0_2px_var(--tw-border-inverse-focus)]',
  MAKE_IT_POP:
    'border-none outline-none transition-all duration-500 ease-[cubic-bezier(0.2,2.3,0.4,1.2)] before:content-[""] before:absolute before:z-[-1] before:inset-[-4px] before:filter before:blur-[0.05rem] before:rounded-[16px] before:transition-all before:duration-200 before:ease-in-out before:[animation:rotation_2s_forwards_infinite] after:content-[""] after:absolute after:z-[-1] after:inset-0 after:bg-background-primary after:filter after:blur-[0.1rem] after:rounded-[12px] hover:scale-105 hover:text-black hover:bg-background-hover',
  LINK: 'p-0 text-secondary-inverse !bg-transparent border-none rounded-none enabled:hover:border-b enabled:hover:border-secondary enabled:focus:border-b enabled:focus:border-secondary',
};

const Button = memo(
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: pre-existing
  forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    const Icon = 'Icon' in props ? props.Icon : undefined;
    const badge = 'badge' in props ? props.badge : false;
    const badgeColor = 'badgeColor' in props ? props.badgeColor : undefined;
    const {
      label,
      variant = 'PRIMARY',
      inverse = false,
      size = 'regular',
      loading,
      disabled,
      fullWidth,
      to,
      iconColor: givenIconColor,
      backgroundColor,
      iconSize,
      'aria-label': explicitAriaLabel,
      className,
      style,
      labelColor,
      ...restProps
    } = props;

    const dynamicStyle: React.CSSProperties = {
      ...(backgroundColor && { backgroundColor: colorCssVar(backgroundColor) }),
      ...style,
    };

    // Debounce loading by 100ms to allow for fade animation
    const debouncedLoading = useDebouncedState(loading, 100);
    const showLoading = loading || debouncedLoading;
    const loadingVisible = !!debouncedLoading;

    const iconColor = useMemo(() => {
      if (givenIconColor) {
        return givenIconColor;
      }
      if (disabled && !inverse) {
        return 'iconDisabled';
      }
      if (disabled && inverse) {
        return 'iconInverseDisabled';
      }
      if (inverse) {
        return variantToInverseButtonIconColor[variant];
      }

      return variantToButtonIconColor[variant];
    }, [disabled, givenIconColor, inverse, variant]);

    const renderAdornment = ({
      adornment,
      key,
    }: {
      adornment: ButtonIcon;
      key?: 'start' | 'end';
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
    }) => {
      if (!adornment) {
        return null;
      }
      if (React.isValidElement(adornment)) {
        return adornment;
      }
      if (typeof adornment === 'function') {
        const Icon = adornment;
        return (
          <Icon
            className={
              key ? `button${upCaseFirstLetter(key)}Adornment` : undefined
            }
            color={iconColor}
            size={iconSize}
          />
        );
      }
      if (typeof adornment === 'string') {
        try {
          emojiSchema.parse(adornment);
          return adornment;
        } catch (_e) {
          console.error(
            `Only emojis are supported as string \`${
              key === undefined
                ? 'Icon'
                : key === 'start'
                  ? 'startAdornment'
                  : 'endAdornment'
            }\` for Button`,
          );
        }
      }
      return null;
    };

    const a11yProps = {
      'aria-disabled': loading || disabled,
      'aria-label': loading ? 'Loading' : explicitAriaLabel || label,
    };

    const variantClasses = inverse
      ? VARIANT_INVERSE_CLASSES[variant]
      : VARIANT_CLASSES[variant];

    const disabledClasses = cn(
      DISABLED_BUTTON,
      inverse
        ? 'disabled:text-inverse-disabled disabled:bg-background-accent-inverse disabled:hover:!bg-background-accent-inverse'
        : 'disabled:text-disabled disabled:bg-background-disabled-subtle disabled:hover:!bg-background-disabled-subtle',
    );

    /* The new version of the deprecated IconButton */
    if (Icon) {
      return (
        <button
          ref={ref}
          className={cn(
            BASE_BUTTON,
            'flex items-center justify-center w-[40px] h-[40px] p-[10px]',
            size === 'small' && 'w-[32px] h-[32px] p-[6px]',
            variantClasses,
            disabledClasses,
            fullWidth && 'w-full',
            className,
          )}
          disabled={disabled}
          style={dynamicStyle}
          {...a11yProps}
          {...(restProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          <span
            className={cn(
              'flex flex-row gap-xs1 items-center justify-center h-sm3 transition-opacity duration-100 ease-[cubic-bezier(0.39,0.575,0.565,1)]',
              size === 'small' && 'h-[20px]',
              loading ? 'opacity-0' : 'opacity-100',
            )}
          >
            {renderAdornment({ adornment: Icon })}
          </span>
          {showLoading && (
            <LoadingIcon
              className={cn(
                'absolute top-[52%] left-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-100 ease-[cubic-bezier(0.39,0.575,0.565,1)]',
                loadingVisible ? 'opacity-100' : 'opacity-0',
              )}
            />
          )}
          {badge && (
            <span
              className="absolute top-xxs2 right-xxs2 w-xs2 h-xs2 rounded-full bg-[var(--tw-red-50)]"
              style={
                badgeColor
                  ? { backgroundColor: colorCssVar(badgeColor) }
                  : undefined
              }
            />
          )}
        </button>
      );
    }

    const startAdornment = renderAdornment({
      adornment: 'startAdornment' in props && props.startAdornment,
      key: 'start',
    });
    const endAdornment = renderAdornment({
      adornment: 'endAdornment' in props && props.endAdornment,
      key: 'end',
    });
    const hasAdornment = Boolean(startAdornment || endAdornment);

    return (
      <ConditionalWrapper
        condition={!!to}
        wrapper={children => <Clickable to={to}>{children}</Clickable>}
      >
        <button
          ref={ref}
          className={cn(
            BASE_BUTTON,
            size === 'small' && 'py-xs2 px-sm2',
            fullWidth && 'w-full',
            variantClasses,
            disabledClasses,
            className,
          )}
          disabled={disabled}
          style={dynamicStyle}
          {...a11yProps}
          {...(restProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          <span
            className={cn(
              'flex flex-row gap-xs1 items-center justify-center h-sm3 transition-opacity duration-100 ease-[cubic-bezier(0.39,0.575,0.565,1)]',
              size === 'small' && 'h-[20px]',
              loading ? 'opacity-0' : 'opacity-100',
            )}
          >
            {startAdornment}
            {label ? (
              <span
                className={cn(
                  'min-w-fit ui-body-semibold buttonLabel relative top-1/2 -translate-y-[65%]',
                  size === 'small' && 'text-body-small',
                  hasAdornment && 'mx-xs1',
                  disabled && 'text-disabled',
                )}
                style={
                  labelColor && !disabled
                    ? { color: colorCssVar(labelColor) }
                    : undefined
                }
              >
                {label}
              </span>
            ) : null}
            {endAdornment}
          </span>
          {showLoading && (
            <LoadingIcon
              className={cn(
                'absolute top-[52%] left-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-100 ease-[cubic-bezier(0.39,0.575,0.565,1)]',
                loadingVisible ? 'opacity-100' : 'opacity-0',
              )}
            />
          )}
        </button>
      </ConditionalWrapper>
    );
  }),
);

export default Button;
